import _defineProperty from "/var/app/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/var/app/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from "react";
import * as ReactDOM from "react-dom";
import { LightboxDefaultProps } from "../../props.js";
import { createModule } from "../config.js";
import { clsx, composePrefix, cssClass, cssVar } from "../utils.js";
import { useEventCallback, useMotionPreference } from "../hooks/index.js";
import { useEvents, useTimeouts } from "../contexts/index.js";
import { ACTION_CLOSE, CLASS_NO_SCROLL_PADDING, MODULE_PORTAL } from "../consts.js";

var cssPrefix = function cssPrefix(value) {
  return composePrefix(MODULE_PORTAL, value);
};

var setAttribute = function setAttribute(element, attribute, value) {
  var previousValue = element.getAttribute(attribute);
  element.setAttribute(attribute, value);
  return function () {
    if (previousValue) {
      element.setAttribute(attribute, previousValue);
    } else {
      element.removeAttribute(attribute);
    }
  };
};

export var Portal = function Portal(_ref) {
  var children = _ref.children,
      animation = _ref.animation,
      styles = _ref.styles,
      className = _ref.className,
      on = _ref.on,
      close = _ref.close;

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      mounted = _React$useState2[0],
      setMounted = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      visible = _React$useState4[0],
      setVisible = _React$useState4[1];

  var cleanup = React.useRef([]);

  var _useTimeouts = useTimeouts(),
      setTimeout = _useTimeouts.setTimeout;

  var _useEvents = useEvents(),
      subscribe = _useEvents.subscribe;

  var fadeAnimationDuration = !useMotionPreference() ? animation.fade : 0;
  React.useEffect(function () {
    setMounted(true);
    return function () {
      setMounted(false);
      setVisible(false);
    };
  }, []);
  var handleClose = useEventCallback(function () {
    var _a;

    setVisible(false);
    (_a = on.exiting) === null || _a === void 0 ? void 0 : _a.call(on);
    setTimeout(function () {
      var _a;

      (_a = on.exited) === null || _a === void 0 ? void 0 : _a.call(on);
      close();
    }, fadeAnimationDuration);
  });
  React.useEffect(function () {
    return subscribe(ACTION_CLOSE, handleClose);
  }, [subscribe, handleClose]);
  var handleEnter = useEventCallback(function (node) {
    var _a, _b, _c;

    node.scrollTop;
    setVisible(true);
    (_a = on.entering) === null || _a === void 0 ? void 0 : _a.call(on);
    var elements = (_c = (_b = node.parentNode) === null || _b === void 0 ? void 0 : _b.children) !== null && _c !== void 0 ? _c : [];

    for (var i = 0; i < elements.length; i += 1) {
      var element = elements[i];

      if (["TEMPLATE", "SCRIPT", "STYLE"].indexOf(element.tagName) === -1 && element !== node) {
        cleanup.current.push(setAttribute(element, "inert", "true"));
        cleanup.current.push(setAttribute(element, "aria-hidden", "true"));
      }
    }

    setTimeout(function () {
      var _a;

      (_a = on.entered) === null || _a === void 0 ? void 0 : _a.call(on);
    }, fadeAnimationDuration);
  });
  var handleExit = useEventCallback(function () {
    cleanup.current.forEach(function (clean) {
      return clean();
    });
    cleanup.current = [];
  });
  var handleRef = React.useCallback(function (node) {
    if (node) {
      handleEnter(node);
    } else {
      handleExit();
    }
  }, [handleEnter, handleExit]);
  return mounted ? ReactDOM.createPortal(React.createElement("div", {
    ref: handleRef,
    className: clsx(className, cssClass("root"), cssClass(cssPrefix()), cssClass(CLASS_NO_SCROLL_PADDING), visible && cssClass(cssPrefix("open"))),
    role: "presentation",
    "aria-live": "polite",
    style: _objectSpread(_objectSpread({}, animation.fade !== LightboxDefaultProps.animation.fade ? _defineProperty({}, cssVar("fade_animation_duration"), "".concat(fadeAnimationDuration, "ms")) : null), styles.root)
  }, children), document.body) : null;
};
export var PortalModule = createModule(MODULE_PORTAL, Portal);